/**
 * The listing contract.
 *
 * **This file plus `services/api/src/sources/port.ts` is the liftable asset.**
 * Every adapter is written against these types and imports nothing else from
 * the application, so the dependency arrow points `sources → shared` and never
 * `domain → sources`. When extraction is earned, the standalone unit takes
 * this file, `sources/`, and its own schema — and Homestead Hunter calls it
 * over plain HTTP.
 *
 * ── A Listing is a posting. An Opportunity is the thing you buy. ─────────────
 *
 * One row per posting on one source: what it says, what it costs, who posted
 * it, whether it is still up. Everything that is a property of the physical
 * object rather than of an advertisement — the pipeline, the score, the landed
 * cost, the offers — lives on the Opportunity. A single posting cannot
 * describe two different physical trailers, which is why the relationship is
 * 1:N through a nullable FK and not a join table.
 *
 * Putting the seller on the LISTING rather than on the opportunity is what
 * makes the fraud signal expressible at all: two listings under one
 * opportunity with two different sellers *is* the flag.
 *
 * ── §3.1, §3.10, §5.2, §10.2, §12 ───────────────────────────────────────────
 */

/* ────────────────────────────────────────────────────────────────────────────
 * Vocabularies
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * What we might live in. Grouped by what it costs to move and how long it
 * lasts, which is why `container` and `casita` are separate from
 * `travel_trailer` — they fail differently and they are hauled differently.
 */
export const STRUCTURE_TYPES = [
  'travel_trailer',
  'fifth_wheel',
  'motorhome',
  'mobile_home',
  'modular_home',
  'park_model',
  'container',
  'cargo_trailer',
  'tiny_house',
  'casita',
  'other',
] as const
export type StructureType = (typeof STRUCTURE_TYPES)[number]

/** Worst to best. The score maps these onto 10 / 25 / 50 / 75 / 88 / 100. */
export const CONDITION_GRADES = ['project', 'poor', 'fair', 'good', 'very_good', 'excellent'] as const
export type ConditionGrade = (typeof CONDITION_GRADES)[number]

/**
 * `not_required` is a real answer, not a missing one: a container has no
 * title and never needed one, and scoring it as `none` would invent a problem.
 */
export const TITLE_STATUSES = [
  'clean',
  'in_hand',
  'not_required',
  'bonded',
  'lien',
  'salvage',
  'lost',
  'none',
  'unknown',
] as const
export type TitleStatus = (typeof TITLE_STATUSES)[number]

/** What the STRUCTURE already has. What the LAND has is the land profile. */
export const UTILITY_KEYS = ['plumbing', 'water', 'electric', 'ac', 'septic', 'kitchen', 'bathroom'] as const
export type UtilityKey = (typeof UTILITY_KEYS)[number]

/** The named damage flags the repair estimate and the condition factor read. */
export const DAMAGE_FLAGS = ['roof', 'floor', 'water_damage', 'mold'] as const
export type DamageFlag = (typeof DAMAGE_FLAGS)[number]

/**
 * All ten provenance types, and the whole of `opportunity_facts.provenance`.
 *
 * The order here is meaningless; the RANK is what matters and it lives in
 * `facts.ts`, on the server, because it is enforced on write.
 */
export const PROVENANCES = [
  'SOURCE_LISTING',
  'SELLER_MESSAGE',
  'PHONE_CALL',
  'USER_OBSERVATION',
  'INSPECTION',
  'DOCUMENT',
  'AI_INFERENCE',
  'SYSTEM_ESTIMATE',
  'MANUAL_ESTIMATE',
  'VERIFIED_RECORD',
] as const
export type Provenance = (typeof PROVENANCES)[number]

/**
 * The six certainty categories the UI must be able to show.
 *
 * `AI_INFERENCE` **never** collapses into `OUR_ESTIMATE`. An AI-inferred value
 * must be visibly AI-inferred at the moment a human reads it, not merely
 * lower-ranked at the moment it was written.
 */
export const CERTAINTIES = [
  'VERIFIED',
  'SOURCE_FACT',
  'SELLER_CLAIM',
  'OUR_ESTIMATE',
  'AI_INFERENCE',
  'UNKNOWN',
] as const
export type Certainty = (typeof CERTAINTIES)[number]

/**
 * The twenty-five attributes that carry provenance.
 *
 * `square_feet` is here because a container's or casita's advertised square
 * footage is frequently the only size figure a listing gives.
 *
 * **Price is deliberately absent.** It has a better mechanism in
 * `listing_snapshots`, which records every change with a delta; a fact row
 * would give one current value and lose the history the negotiation context is
 * computed from.
 *
 * `has_kitchen` and `has_bathroom` are typed columns WITHOUT provenance, set
 * by the livability editor — a person ticking a box is not making a claim
 * anyone needs to trace.
 */
export const FACT_ATTRIBUTES = [
  'structure_type',
  'year',
  'manufacturer',
  'model',
  'length_ft',
  'width_ft',
  'height_ft',
  'square_feet',
  'weight_lbs',
  'bedrooms',
  'bathrooms',
  'condition',
  'roof_condition',
  'known_damage',
  'title_status',
  'vin',
  'towable',
  'wheels_present',
  'axles_present',
  'hitch_present',
  'has_electric',
  'has_plumbing',
  'has_water',
  'has_septic',
  'has_ac',
] as const
export type FactAttribute = (typeof FACT_ATTRIBUTES)[number]

export function isStructureType(v: unknown): v is StructureType {
  return typeof v === 'string' && (STRUCTURE_TYPES as readonly string[]).includes(v)
}
export function isConditionGrade(v: unknown): v is ConditionGrade {
  return typeof v === 'string' && (CONDITION_GRADES as readonly string[]).includes(v)
}
export function isTitleStatus(v: unknown): v is TitleStatus {
  return typeof v === 'string' && (TITLE_STATUSES as readonly string[]).includes(v)
}
export function isProvenance(v: unknown): v is Provenance {
  return typeof v === 'string' && (PROVENANCES as readonly string[]).includes(v)
}
export function isFactAttribute(v: unknown): v is FactAttribute {
  return typeof v === 'string' && (FACT_ATTRIBUTES as readonly string[]).includes(v)
}

/* ────────────────────────────────────────────────────────────────────────────
 * The availability lifecycle — a THIRD axis
 * ────────────────────────────────────────────────────────────────────────── */

export const AVAILABILITIES = [
  'active',
  'unknown',
  'pending',
  'sold',
  'expired',
  'removed',
  'reposted',
] as const
export type Availability = (typeof AVAILABILITIES)[number]

export function isAvailability(v: unknown): v is Availability {
  return typeof v === 'string' && (AVAILABILITIES as readonly string[]).includes(v)
}

/** Availability is only ever changed by one of these. */
export type AvailabilityActor =
  /** A person said so. */
  | 'human'
  /** A check ran and confirmed the listing is still up. */
  | 'observation_confirmed'
  /** A check ran and could not see it. NEVER `removed` on its own. */
  | 'observation_missed'
  /** The miss-count and age thresholds are both satisfied. */
  | 'aged_out'

/** `miss_count` must reach this before `removed` is even considered. */
export const REMOVED_MIN_MISSES = 3

/** …and this long must have passed since the last confirmed sighting. */
export const REMOVED_MIN_AGE_MS = 48 * 60 * 60 * 1000

/**
 * §5.2's transition table, as data.
 *
 * The value is the set of actors permitted to make that move. An absent key is
 * an illegal move.
 *
 * **A failed or missed observation sets `unknown`, never `removed`.** §B says
 * in as many words not to assume a listing disappearing means it sold, and in
 * Phase 1 nothing re-checks a listing at all, so every non-`active` value
 * arrives from a person. `removed` additionally requires the miss count AND
 * the 48 hours, so Phase 2's first flaky fetch cannot kill a live negotiation.
 *
 * `reposted` is a dead end on this table because it is also a POINTER: the new
 * listing carries `reposted_from_listing_id` and the two share an opportunity.
 * The old row is finished; the story continues on the new one.
 */
export const AVAILABILITY_TRANSITIONS: Partial<
  Record<Availability, Partial<Record<Availability, readonly AvailabilityActor[]>>>
> = {
  active: {
    unknown: ['observation_missed'],
    pending: ['human'],
    sold: ['human'],
    expired: ['human'],
    reposted: ['human'],
  },
  unknown: {
    active: ['human', 'observation_confirmed'],
    unknown: ['observation_missed'],
    pending: ['human'],
    sold: ['human'],
    expired: ['human'],
    removed: ['aged_out', 'human'],
    reposted: ['human'],
  },
  pending: {
    active: ['human'],
    sold: ['human'],
    expired: ['human'],
    reposted: ['human'],
  },
  sold: {
    // Relisted. It happens, and it is how a "sold" seller comes back in March.
    active: ['human'],
    reposted: ['human'],
  },
  expired: {
    active: ['human'],
    sold: ['human'],
    removed: ['human'],
    reposted: ['human'],
  },
  removed: {
    // It reappeared. Nothing was destroyed, so nothing needs recovering.
    active: ['human'],
    sold: ['human'],
    reposted: ['human'],
  },
  reposted: {},
}

export interface AvailabilityContext {
  /** After this move. The caller increments before asking. */
  missCount?: number
  /** Epoch ms, or `null` when it has never been confirmed. */
  lastConfirmedActiveAt?: number | null
  /** Epoch ms. Injected, never `Date.now()` inside a pure function. */
  now?: number
}

export type AvailabilityOutcome =
  | { ok: true }
  | { ok: false; code: 'illegal' | 'too_soon'; message: string }

/**
 * May this listing move from `from` to `to`, by this actor, right now?
 *
 * Pure and total. The `too_soon` refusal is the one that matters: it is what
 * stops three flaky fetches in ten minutes from marking a live listing gone.
 */
export function canChangeAvailability(
  from: Availability,
  to: Availability,
  actor: AvailabilityActor,
  context: AvailabilityContext = {},
): AvailabilityOutcome {
  if (!isAvailability(from) || !isAvailability(to)) {
    return { ok: false, code: 'illegal', message: 'Not an availability value.' }
  }
  if (from === to && !(from === 'unknown' && actor === 'observation_missed')) {
    return { ok: false, code: 'illegal', message: 'Nothing would change.' }
  }

  const allowed = AVAILABILITY_TRANSITIONS[from]?.[to]
  if (!allowed || !allowed.includes(actor)) {
    return {
      ok: false,
      code: 'illegal',
      message: `A listing cannot go from "${from}" to "${to}"${actor === 'human' ? '' : ' automatically'}.`,
    }
  }

  if (to === 'removed' && actor === 'aged_out') {
    const misses = context.missCount ?? 0
    if (misses < REMOVED_MIN_MISSES) {
      return {
        ok: false,
        code: 'too_soon',
        message: `Seen missing ${misses} time${misses === 1 ? '' : 's'}; ${REMOVED_MIN_MISSES} are needed before a listing is treated as removed.`,
      }
    }
    const last = context.lastConfirmedActiveAt
    const now = context.now ?? 0
    if (last === null || last === undefined || now - last < REMOVED_MIN_AGE_MS) {
      return {
        ok: false,
        code: 'too_soon',
        message: 'A listing is not treated as removed until 48 hours after it was last confirmed active.',
      }
    }
  }

  return { ok: true }
}

/**
 * Is this listing still worth ranking and alerting on?
 *
 * Availability never moves the pipeline — that would let one person's note
 * kill a live negotiation — but it DOES gate the feed's default sort and every
 * instant alert. A `sold` listing still computing a 92 and sorting to the top
 * of the feed is the failure this guards against.
 */
export function countsAsLive(availability: Availability): boolean {
  return availability === 'active' || availability === 'unknown'
}

/**
 * "Every listing under this opportunity is gone." Advisory only: the feed
 * reports `mayBeGone: true` and offers "Mark Lost?". **Nothing auto-
 * transitions** — a seller who marks it pending during our own negotiation
 * must not close our own deal for us.
 */
export function mayBeGone(availabilities: readonly Availability[]): boolean {
  return availabilities.length > 0 && !availabilities.some(countsAsLive)
}

/* ────────────────────────────────────────────────────────────────────────────
 * The normalized listing
 * ────────────────────────────────────────────────────────────────────────── */

/** How a listing got here. All five are a person; none of them fetch. */
export const INTAKE_METHODS = ['manual', 'paste', 'share', 'ext', 'adapter'] as const
export type IntakeMethod = (typeof INTAKE_METHODS)[number]

/** What we are allowed to keep, from the adapter's own declaration. */
export const RETENTIONS = ['full', 'metadata', 'link_only'] as const
export type Retention = (typeof RETENTIONS)[number]

export interface NormalizedImage {
  /** Where it lives on the source. Never fetched in Phase 1. */
  remoteUrl?: string | null
  /** An `uploads` id, when a person uploaded the photo themselves. */
  uploadId?: string | null
  ordinal: number
  width?: number | null
  height?: number | null
  contentType?: string | null
  /** Hex sha256 of the bytes. The +30 duplicate signal. */
  byteSha256?: string | null
}

export interface NormalizedSeller {
  name?: string | null
  /** Raw as typed. `shared/phone.normalizePhone` is applied downstream. */
  phone?: string | null
  email?: string | null
  /** A marketplace profile URL or handle. */
  profileUrl?: string | null
}

/**
 * What an adapter hands `ingest.ts`. **No `Env`, no `D1Database`, no
 * `Request`, no `SessionUser`** anywhere near it: all persistence, capability
 * gating, auditing and notification happen outside `sources/`.
 *
 * Dates crossing this boundary are **epoch milliseconds**. Business-date
 * conversion happens app-side, which is what keeps CLAUDE.md rule 12 intact —
 * an adapter has no idea where the business is and must not guess.
 */
export interface NormalizedListing {
  /** The `sources` row this came from. */
  sourceId: string
  /** The source's own id for the posting, when it has one. */
  sourceListingId?: string | null
  /** The URL a person would open. Stored; never fetched for a closed source. */
  sourceUrl?: string | null
  /** `normalizeUrlKey(sourceUrl)`. The partial UNIQUE index's value. */
  urlKey?: string | null
  /** The client's `crypto.randomUUID()`, so a replayed POST is a no-op. */
  idempotencyKey?: string | null

  title: string
  description?: string | null
  category?: string | null
  subcategory?: string | null

  /** Integer cents. Never a float, never dollars. */
  priceCents?: number | null
  currency?: string | null

  locationText?: string | null
  municipality?: string | null
  island?: string | null
  region?: string | null

  seller?: NormalizedSeller | null
  images?: NormalizedImage[]

  availability?: Availability
  /** Epoch ms, from the source. Not our clock. */
  postedAt?: number | null

  intakeMethod: IntakeMethod
  retention?: Retention

  /**
   * What the adapter believes about the STRUCTURE, each landing as a fact row
   * at `SOURCE_LISTING` provenance — rank 1, which by §3.3 can never displace
   * an inspection or a document.
   */
  attributes?: Partial<Record<FactAttribute, string | number | boolean | null>>
}

/* ────────────────────────────────────────────────────────────────────────────
 * Normalisation helpers
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Tracking parameters that identify the CLICK, not the listing.
 *
 * The same posting shared from Messenger and from a browser differs only in
 * these, and leaving them in means `url_key` no longer dedupes — which turns
 * one opportunity into two and quietly breaks the whole point of the index.
 */
const TRACKING_PARAMS = [
  'fbclid',
  'gclid',
  'msclkid',
  'igshid',
  'mc_cid',
  'mc_eid',
  'ref',
  'referrer',
  'source',
  'share',
  'mibextid',
  'rdt',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'utm_id',
]

/**
 * A stable key for "the same posting", or `null`.
 *
 * Lowercases the host, drops `www.`, drops the scheme's default port, strips
 * tracking parameters, sorts what remains, drops the fragment, and removes a
 * trailing slash.
 *
 * **Deliberately does NOT drop every query parameter.** Some marketplaces
 * carry the listing id there, and a key that collapses two different postings
 * into one would auto-attach them to the same opportunity with no way to tell.
 * Losing a dedupe is recoverable; merging two structures is not.
 */
export function normalizeUrlKey(raw: string | null | undefined): string | null {
  const value = String(raw ?? '').trim()
  if (value === '') return null

  let url: URL
  try {
    url = new URL(value)
  } catch {
    return null
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

  const host = url.hostname.toLowerCase().replace(/^www\./, '')
  if (host === '') return null

  const params = new URLSearchParams()
  const kept = [...url.searchParams.entries()]
    .filter(([k]) => !TRACKING_PARAMS.includes(k.toLowerCase()))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
  for (const [k, v] of kept) params.append(k, v)

  const path = url.pathname.replace(/\/+$/, '')
  const query = params.toString()
  const port = url.port && url.port !== '80' && url.port !== '443' ? `:${url.port}` : ''

  return `${host}${port}${path}${query ? `?${query}` : ''}`
}

/**
 * Whitespace-normalised, control-stripped, length-capped text.
 *
 * The cap is not tidiness. A pasted marketplace description arrives with the
 * whole page in it; a 200 KB description in a feed row is a slow query and a
 * fat response on Vieques connectivity, and `feed.ts` never selects it at all.
 */
export function normalizeText(raw: string | null | undefined, maxLength = 20_000): string | null {
  if (raw === null || raw === undefined) return null
  const value = String(raw)
    // Control characters, but keep newline and tab — a pasted listing's line
    // breaks are the only structure it has.
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
    .replace(/\r\n?/g, '\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .split('\n')
    .map((line) => line.trim())
    .join('\n')
    .trim()
  if (value === '') return null
  return value.length > maxLength ? value.slice(0, maxLength) : value
}

export interface ListingProblem {
  field: string
  code: 'required' | 'too_long' | 'not_an_integer' | 'negative' | 'unknown_value' | 'not_a_number'
  message: string
}

export type ValidationResult =
  | { ok: true; listing: NormalizedListing }
  | { ok: false; problems: ListingProblem[] }

const MAX_TITLE = 300

/**
 * Validate and clean what an adapter produced. Total — collects every problem
 * rather than throwing on the first, because the intake screen shows them all
 * at once and a person fixing one field at a time on a phone gives up.
 *
 * Returns a NEW listing with text normalised and price coerced, so a caller
 * cannot accidentally persist the raw one.
 */
export function validateNormalizedListing(input: NormalizedListing): ValidationResult {
  const problems: ListingProblem[] = []

  const sourceId = String(input.sourceId ?? '').trim()
  if (sourceId === '') {
    problems.push({ field: 'sourceId', code: 'required', message: 'A listing must name its source.' })
  }

  const title = normalizeText(input.title, MAX_TITLE)
  if (title === null) {
    problems.push({ field: 'title', code: 'required', message: 'A listing needs a title.' })
  }

  let priceCents = input.priceCents ?? null
  if (priceCents !== null && priceCents !== undefined) {
    if (typeof priceCents !== 'number' || !Number.isFinite(priceCents)) {
      problems.push({ field: 'priceCents', code: 'not_a_number', message: 'Price must be a number of cents.' })
      priceCents = null
    } else if (!Number.isSafeInteger(priceCents)) {
      problems.push({
        field: 'priceCents',
        code: 'not_an_integer',
        message: 'Price is integer cents. A float in a money path is a defect.',
      })
      priceCents = null
    } else if (priceCents < 0) {
      problems.push({ field: 'priceCents', code: 'negative', message: 'A price cannot be negative.' })
      priceCents = null
    }
  }

  const availability = input.availability ?? 'active'
  if (!isAvailability(availability)) {
    problems.push({ field: 'availability', code: 'unknown_value', message: `"${String(availability)}" is not an availability.` })
  }

  if (!(INTAKE_METHODS as readonly string[]).includes(input.intakeMethod)) {
    problems.push({
      field: 'intakeMethod',
      code: 'unknown_value',
      message: `"${String(input.intakeMethod)}" is not an intake method.`,
    })
  }

  const attributes: Partial<Record<FactAttribute, string | number | boolean | null>> = {}
  for (const [key, value] of Object.entries(input.attributes ?? {})) {
    if (!isFactAttribute(key)) {
      problems.push({ field: `attributes.${key}`, code: 'unknown_value', message: `"${key}" is not a fact attribute.` })
      continue
    }
    attributes[key] = value as string | number | boolean | null
  }

  if (problems.length > 0) return { ok: false, problems }

  return {
    ok: true,
    listing: {
      ...input,
      sourceId,
      title: title as string,
      description: normalizeText(input.description),
      locationText: normalizeText(input.locationText, 300),
      priceCents,
      availability,
      urlKey: input.urlKey ?? normalizeUrlKey(input.sourceUrl),
      attributes,
    },
  }
}
